import * as XLSX from 'xlsx';
import * as Yup from 'yup';

import { createEmptyLocalizedMap, LocalizedStringsMap, SupportedLanguageKey } from 'core/constants';
import { UploadedQuote } from './index';

interface IExcelQuoteRawRow {
	startDate?: number | string;
	message?: string;
	english?: string;
	french?: string;
	hebrew?: string;
	russian?: string;
	ukrainian?: string;
}

const MAX_QUOTE_LENGTH = 80;

const excelDateToJSDate = (excelDate: string | number): Date => {
	if (typeof excelDate === 'number') {
		const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // December 30, 1899
		const millisecondsPerDay = 24 * 60 * 60 * 1000;

		const convertedDate = new Date(excelEpoch.getTime() + excelDate * millisecondsPerDay);

		return new Date(convertedDate.getUTCFullYear(), convertedDate.getUTCMonth(), convertedDate.getUTCDate());
	}

	const trimmed = String(excelDate).trim();
	// Support DD.MM.YYYY or DD/MM/YYYY string format if typed as text in Excel
	const dmyMatch = trimmed.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
	if (dmyMatch) {
		const [, day, month, year] = dmyMatch;
		return new Date(Number(year), Number(month) - 1, Number(day));
	}

	return new Date(trimmed);
};

const getParsedData = (sheet: XLSX.WorkSheet) => {
	const rowsArray = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as Array<Array<string>>;
	const rowIndex = rowsArray.findIndex(
		row =>
			Array.isArray(row) &&
			row.some(
				cell =>
					String(cell || '')
						.trim()
						.toLowerCase() === 'start date'
			)
	);
	const ref = sheet['!ref']!;

	let range = ref;

	if (rowIndex > 0 && ref) {
		const [refStart, refEnd] = ref.split(':');
		const colLetters = refStart.replace(/[0-9]/g, '') || 'A';
		const newRefStart = `${colLetters}${rowIndex + 1}`;
		range = `${newRefStart}:${refEnd}`;
	}

	return XLSX.utils.sheet_to_json(sheet, {
		range,
		defval: '',
		blankrows: false,
	}) as Array<Record<string, any>>;
};

export const parseQuotesXlsx = (file: File) => {
	return new Promise<Array<UploadedQuote>>(resolve => {
		const reader = new FileReader();
		reader.onload = e => {
			const data = e.target?.result;
			const excelData = XLSX.read(data, { type: 'binary' });
			const sheetName = excelData.SheetNames[0];
			const sheet = excelData.Sheets[sheetName];

			const parsedData = getParsedData(sheet);

			const normalizeParsedData = parsedData.map(parsedUserValuesObj => {
				let normalizedUserValuesObj = {} as IExcelQuoteRawRow;

				Object.keys(parsedUserValuesObj).forEach(key => {
					const normalizeKeyName = key
						.trim()
						.split(/\s+/)
						.filter(k => k !== '-' && k !== '')
						.map((word, idx) => {
							word = word.toLowerCase();
							if (idx !== 0) word = word[0].toUpperCase() + word.slice(1);
							return word;
						})
						.join('');

					normalizedUserValuesObj = {
						...normalizedUserValuesObj,
						[normalizeKeyName]: parsedUserValuesObj[key],
					};
				});

				return normalizedUserValuesObj;
			});

			const importedDailyQuotes: Array<UploadedQuote> = normalizeParsedData.map(row => {
				const messages: LocalizedStringsMap = createEmptyLocalizedMap();

				const englishValue =
					row.english !== undefined && String(row.english).trim() !== ''
						? String(row.english)
						: String(row.message ?? '');

				messages.en = englishValue.trim();
				messages.fr = String(row.french ?? '').trim();
				messages.he = String(row.hebrew ?? '').trim();
				messages.ru = String(row.russian ?? '').trim();
				messages.uk = String(row.ukrainian ?? '').trim();

				return {
					messages,
					startDate:
						row.startDate !== undefined && String(row.startDate).trim() !== ''
							? excelDateToJSDate(row.startDate)
							: undefined,
				};
			});

			resolve(importedDailyQuotes);
		};

		reader.readAsBinaryString(file);
	});
};

export const quoteValidation = Yup.object({
	messages: Yup.object({
		en: Yup.string()
			.trim()
			.max(MAX_QUOTE_LENGTH, 'English quote message is too long (max 80 characters)')
			.required('English quote message is required'),
		fr: Yup.string().trim().max(MAX_QUOTE_LENGTH, 'French quote message is too long (max 80 characters)'),
		he: Yup.string().trim().max(MAX_QUOTE_LENGTH, 'Hebrew quote message is too long (max 80 characters)'),
		ru: Yup.string().trim().max(MAX_QUOTE_LENGTH, 'Russian quote message is too long (max 80 characters)'),
		uk: Yup.string().trim().max(MAX_QUOTE_LENGTH, 'Ukrainian quote message is too long (max 80 characters)'),
	}),
	startDate: Yup.date().typeError('Invalid date').required('Date is required'),
});

export const validateUploadedQuotesList = async (
	quotes: Array<UploadedQuote>
): Promise<{ validatedQuotes: Array<UploadedQuote>; errorsCount: number }> => {
	let errorsCount = 0;
	const uniqueDates = new Set<string>();
	const duplicateDates = new Set<string>();

	quotes.forEach(quote => {
		if (quote?.startDate && !Number.isNaN(quote.startDate.getTime())) {
			const y = quote.startDate.getFullYear();
			const m = String(quote.startDate.getMonth() + 1).padStart(2, '0');
			const d = String(quote.startDate.getDate()).padStart(2, '0');
			const dateKey = `${y}-${m}-${d}`;
			if (uniqueDates.has(dateKey)) {
				duplicateDates.add(dateKey);
			} else {
				uniqueDates.add(dateKey);
			}
		}
	});

	const getDateKey = (date?: Date) => {
		if (!date || Number.isNaN(date.getTime())) return '';
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const d = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${d}`;
	};

	const validatedQuotes = await Promise.all(
		quotes.map(async quote => {
			const { errors: _prevErrors, ...cleanQuote } = quote;
			const dateKey = getDateKey(cleanQuote.startDate);
			const isDuplicateDate = Boolean(dateKey && duplicateDates.has(dateKey));

			try {
				await quoteValidation.validate(cleanQuote, { abortEarly: false });
				if (isDuplicateDate) {
					errorsCount += 1;
					return {
						...cleanQuote,
						errors: { nonUniqueDate: 'Multiple quotes for the same date are not allowed.' },
					};
				}
				return cleanQuote;
			} catch (error) {
				if (error instanceof Yup.ValidationError) {
					errorsCount += 1;
					const validationErrors: Partial<
						Record<SupportedLanguageKey | 'startDate' | 'nonUniqueDate', string>
					> = {};

					error.inner.forEach(innerErr => {
						if (innerErr.path === 'startDate') {
							validationErrors.startDate = innerErr.errors[0];
						} else if (innerErr.path?.startsWith('messages.')) {
							const langKey = innerErr.path.replace('messages.', '') as SupportedLanguageKey;
							validationErrors[langKey] = innerErr.errors[0];
						}
					});

					if (isDuplicateDate) {
						validationErrors.nonUniqueDate = 'Multiple quotes for the same date are not allowed.';
					}

					return {
						...cleanQuote,
						errors: validationErrors,
					};
				}
				return cleanQuote;
			}
		})
	);

	return { validatedQuotes, errorsCount };
};
